/// <amd-module name="Repos/Views/Import/Components/PanelImport/PanelImport" />
import * as React from "react";
import { RepositoryType } from "Repos/Common/Types";
import { getFileName } from "Repos/Common/Util/File";
import { importRepository } from "Repos/Views/Import/Api";
import * as Resources from "Repos/Views/Import/Resources";
import { format } from "VSS/Core/Util/String";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { Components, VssComponent } from "VSS/Platform/Layout";
import { Checkbox } from "VSSUI/Checkbox";
import { Dropdown } from "VSSUI/Dropdown";
import { FormItem } from "VSSUI/FormItem";
import { Link } from "VSSUI/Link";
import { ListSelection } from "VSSUI/List";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Panel } from "VSSUI/Panel";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { TextField } from "VSSUI/TextField";
import { PanelImportProps } from "./PanelImport.Props";

export interface PanelImportState {
    authPassword?: string;
    authRequired: boolean;
    authUsername?: string;
    importStarted?: boolean;
    messageFailure?: React.ReactNode;
    migrateDays: string;
    migrateHistory: boolean;
    repositoryName?: string;
    srcLocation?: string;
    userModifiedRepoName?: boolean;
}

export class PanelImport extends VssComponent<PanelImportProps, PanelImportState> {
    private providerSelection = new ListSelection({
        selectedRanges: [{ beginIndex: 0, endIndex: 0 }],
        selectOnFocus: false
    });

    constructor(props: PanelImportProps) {
        super(props);

        this.state = {
            authRequired: false,
            migrateDays: "30",
            migrateHistory: false,
            repositoryName: props.repositoryName
        };
    }

    public render() {
        return (
            <Observer
                providerSelection={{
                    observableValue: this.providerSelection,
                    filter: () => {
                        this.setState({
                            authPassword: undefined,
                            authRequired: false,
                            authUsername: undefined,
                            messageFailure: undefined,
                            migrateDays: "30",
                            migrateHistory: false,
                            repositoryName: this.props.repositoryName,
                            srcLocation: undefined
                        });
                    }
                }}
            >
                {() => {
                    const {
                        authPassword,
                        authRequired,
                        authUsername,
                        migrateDays,
                        migrateHistory,
                        repositoryName,
                        srcLocation,
                        userModifiedRepoName
                    } = this.state;
                    const selectedProvider = this.providerSelection.value[0].beginIndex === 0 ? RepositoryType.git : RepositoryType.tfvc;
                    let importDisabled = true;

                    // Determine whether or not the user can proceed with the import process.
                    if (repositoryName) {
                        if (selectedProvider === RepositoryType.git) {
                            if (srcLocation && (!authRequired || authPassword)) {
                                importDisabled = false;
                            }
                        } else {
                            if (srcLocation && (!migrateHistory || migrateDays)) {
                                importDisabled = false;
                            }
                        }
                    }

                    return (
                        <Panel
                            defaultActiveElement=".repository-name"
                            footerButtonProps={[
                                { text: Resources.Cancel, onClick: () => this.props.onDismiss() },
                                {
                                    disabled: importDisabled,
                                    primary: true,
                                    onClick: this.onImport,
                                    text: Resources.Import
                                }
                            ]}
                            lightDismiss={false}
                            onDismiss={this.props.onDismiss}
                            overlayContent={this.state.importStarted ? <Spinner label={Resources.ImportStarting} size={SpinnerSize.large} /> : null}
                            titleProps={{ text: selectedProvider === RepositoryType.git ? Resources.ImportFromGit : Resources.ImportFromTFVC }}
                        >
                            <div className="flex-grow rhythm-vertical-16">
                                {this.state.messageFailure ? (
                                    <MessageCard onDismiss={() => this.setState({ messageFailure: undefined })} severity={MessageCardSeverity.Error}>
                                        <div className="flex-column font-size-ms">{this.state.messageFailure}</div>
                                    </MessageCard>
                                ) : null}
                                <FormItem label={Resources.RepoTypeLabel}>
                                    <Dropdown
                                        className="repository-type"
                                        items={[
                                            { iconProps: { iconName: "GitLogo" }, id: RepositoryType.git, text: "Git" },
                                            { iconProps: { iconName: "TFVCLogo" }, id: RepositoryType.tfvc, text: "TFVC" }
                                        ]}
                                        selection={this.providerSelection}
                                    />
                                    {selectedProvider === RepositoryType.tfvc ? (
                                        <MessageCard className="margin-top-8" severity={MessageCardSeverity.Info}>
                                            <FormatComponent format={Resources.TFVCImport}>
                                                <Link
                                                    href="https://docs.microsoft.com/en-us/devops/develop/git/migrate-from-tfvc-to-git"
                                                    target="_blank"
                                                >
                                                    {Resources.Documentation}
                                                </Link>
                                            </FormatComponent>
                                        </MessageCard>
                                    ) : null}
                                </FormItem>
                                <FormItem label={selectedProvider === RepositoryType.git ? Resources.CloneLabel : Resources.PathLabel}>
                                    <TextField
                                        inputClassName="repository-name"
                                        onChange={(e, srcLocation) => {
                                            this.setState({ srcLocation });

                                            // If the caller didnt pass in a fixed repository name and the user
                                            // changes the import location, we will pre-populate the repo name
                                            // from the source location.
                                            if (!this.props.repositoryName) {
                                                let repositoryName = this.state.repositoryName;
                                                if (!userModifiedRepoName) {
                                                    repositoryName = getFileName(srcLocation);
                                                }
                                                this.setState({ repositoryName });
                                            }
                                        }}
                                        placeholder={format(
                                            Resources.Eg,
                                            selectedProvider === RepositoryType.git
                                                ? "https://github.com/Microsoft/vscode.git"
                                                : "$/Contoso/HelloWorld"
                                        )}
                                        spellCheck={false}
                                        value={srcLocation}
                                    />
                                    {selectedProvider === RepositoryType.git ? (
                                        <Checkbox
                                            checked={authRequired}
                                            className="margin-top-8"
                                            label={Resources.RequiresAuthLabel}
                                            onChange={(e, checked) => this.setState({ authRequired: checked })}
                                        />
                                    ) : (
                                        <Checkbox
                                            checked={migrateHistory}
                                            className="margin-top-8"
                                            label={Resources.MigrateHistoryLabel}
                                            onChange={(e, checked) => this.setState({ migrateHistory: checked })}
                                        />
                                    )}
                                </FormItem>
                                {selectedProvider === RepositoryType.git && authRequired ? (
                                    <>
                                        <FormItem label={Resources.UsernameLabel}>
                                            <TextField
                                                onChange={(e, value) => this.setState({ authUsername: value })}
                                                spellCheck={false}
                                                value={authUsername}
                                            />
                                        </FormItem>
                                        <FormItem label={Resources.PasswordPATLabel}>
                                            <TextField
                                                onChange={(e, value) => this.setState({ authPassword: value })}
                                                spellCheck={false}
                                                inputType={"password"}
                                                value={authPassword}
                                            />
                                        </FormItem>
                                    </>
                                ) : null}
                                {selectedProvider === RepositoryType.tfvc && migrateHistory ? (
                                    <FormItem label={Resources.DaysLabel}>
                                        <TextField
                                            onChange={(e, value) => {
                                                const numericalValue = parseInt(value, 10);
                                                if (value === "" || numericalValue.toString() === value) {
                                                    this.setState({ migrateDays: value });
                                                }
                                            }}
                                            spellCheck={false}
                                            value={migrateDays.toString()}
                                        />
                                    </FormItem>
                                ) : null}
                                {!this.props.repositoryName ? (
                                    <FormItem label={Resources.NameLabel}>
                                        <TextField
                                            onChange={(e, value) => this.setState({ repositoryName: value, userModifiedRepoName: true })}
                                            placeholder={Resources.NameYourRepo}
                                            spellCheck={false}
                                            value={repositoryName}
                                        />
                                    </FormItem>
                                ) : null}
                            </div>
                        </Panel>
                    );
                }}
            </Observer>
        );
    }

    private onImport = () => {
        const authRequired = this.state.authRequired;
        const selectedIndex = this.providerSelection.value[0].beginIndex;

        // Begin the import and wait for its success or failure
        const importOperation = importRepository(this.context.pageContext, {
            createRepository: !this.props.repositoryName,
            git:
                selectedIndex === 0
                    ? {
                          cloneUrl: this.state.srcLocation!,
                          authPassword: authRequired ? this.state.authPassword : undefined,
                          authUsername: authRequired ? this.state.authUsername : undefined
                      }
                    : undefined,
            projectId: this.props.projectId,
            projectName: this.props.projectName,
            repositoryName: this.state.repositoryName!,
            tfvc:
                selectedIndex === 1
                    ? {
                          path: this.state.srcLocation!,
                          migrateDays: this.state.migrateHistory ? parseInt(this.state.migrateDays || "30", 10) : 0
                      }
                    : undefined,
            type: selectedIndex === 0 ? RepositoryType.git : RepositoryType.tfvc
        });

        importOperation.catch(error => {
            this.setState({ importStarted: false, messageFailure: error });
        });

        // Notify the caller if they want to monitor the operation.
        this.props.onImport && this.props.onImport(importOperation);
        this.setState({ importStarted: true });
    };
}

Components.add("ms.vss-code-web.panel-import", PanelImport);
